import React from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Typography } from "@material-ui/core";

const useStyles = makeStyles((theme) => ({
    containerReportsInfo: {
        position: "relative",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "flex-start",
        padding: "1rem",
        background: theme.palette.background.paper,
        borderRadius: 12,
        marginBottom: "2rem",
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
        boxSizing: "border-box",
    },
    reportsInfoTitle: {
        fontSize: "1.35rem",
        fontWeight: 525,
        color: theme.palette.text.primary,
        marginBottom: "1rem",
    },
    containerInfoCards: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "flex-start",
        alignItems: "center",
        gap: "2rem",
        marginBottom: "2rem",
    },
    cardInfo: {
        width: "15rem",
        display: "flex",
        justifyContent: "flex-start",
        flexDirection: "column",
        borderRadius: 12,
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
        padding: "1rem",
        boxSizing: "border-box",
    },
    containerTotalReports: {
        background: "rgba(35, 81, 227, 0.10)",  
    },
    containerTotalResolvedReports: {
        background: "rgba(29, 195, 89, 0.10)",
    },
    containerTotalPendingReports: {
        background: "rgba(250, 9, 212, 0.10)",
    },
    containerAverageSolvingTime: {
        background: "rgba(248, 248, 117, 0.15)",
    },
    cardInfoTitle: {
        fontSize: "1rem",
        fontWeight: 525,
        color: theme.palette.text.primary,
        paddingLeft: "0.5rem",
    },
    totalReportsTitle: {
        borderLeft: "3.5px solid #2351E3",
    },
    totalResolvedReportsTitle: {
        borderLeft: "3.5px solid #07c24c",
    },
    totalPendingReportsTitle: {
        borderLeft: "3.5px solid #fc00d6",
    },
    averageSolvingTimeTitle: {
        borderLeft: "3.5px solid #f8f875",
    },
    containerCardInfoValue: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
    },
    cardInfoValue: {
        fontSize: "2rem",
        fontWeight: 600,
        color: theme.palette.text.primary,
        padding: "1.2rem 0",
    },
    containerReportsTypes: {
        display: "flex",
        flexDirection: "row",
        justifyContent: "space-between",
        alignItems: "center",
        gap: "2rem",
    },
    cardReportType: {
        width: "100%",
        display: "flex",
        justifyContent: "flex-start",
        flexDirection: "column",
        borderRadius: 12,
        boxShadow: "0 4px 20px rgba(0, 0, 0, 0.08)",
        padding: "1rem",
        boxSizing: "border-box",
        background: "rgba(35, 81, 227, 0.10)",
    },
    cardReportTypeTitle: {
        height: "3rem",
        fontSize: "1rem",
        fontWeight: 525,
        color: theme.palette.text.primary,
        paddingLeft: "0.5rem",
        borderLeft: "3.5px solid #2351E3",
    },
    containerReportTypeValue: {
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
    },
    reportTypeValue: {
        fontSize: "1.5rem",
        fontWeight: 600,
        color: theme.palette.text.primary,
        padding: "0.75rem 0",
    },
}));

export default function ReportsSummaryComponent({ displayRows }) {
    
    const classes = useStyles();

    // Calculate actual statistics
    const totalReports = displayRows.length;
    const totalResolved = displayRows.filter(r => r.status === 'resolved' || r.status === 'concluded').length;
    const totalPending = displayRows.filter(r => 
        r.status === 'submitted' || r.status === 'pending' || r.status === 'in_review'
    ).length;

    // Calculate average solving time for resolved reports
    const resolvedReports = displayRows.filter(r => 
        (r.status === 'resolved' || r.status === 'concluded') && r.lastUpdatedAt && r.createdAt
    );
    const avgResolutionTime = resolvedReports.length > 0
        ? resolvedReports.reduce((acc, r) => {
            return acc + (new Date(r.lastUpdatedAt) - new Date(r.createdAt));
          }, 0) / resolvedReports.length
        : 0;
    
    // Convert to days
    const avgDays = avgResolutionTime > 0 ? Math.round(avgResolutionTime / (1000 * 60 * 60 * 24)) : 0;

    // Count offenses
    const cheatingCount = displayRows.filter(r => r.offenses?.cheating).length;
    const griefingCount = displayRows.filter(r => r.offenses?.griefing).length;
    const smurfingCount = displayRows.filter(r => r.offenses?.smurfing).length;
    const abusiveCommsCount = displayRows.filter(r => r.offenses?.abusive_comms).length;
    const notTeamPlayerCount = displayRows.filter(r => r.offenses?.not_team_player).length;
    const notVocalCount = displayRows.filter(r => r.offenses?.player_not_vocal).length;

    return (
        <div className={classes.containerReportsInfo}>
            <Typography className={classes.reportsInfoTitle}>Reports Summary</Typography>
            <div className={classes.containerInfoCards}>
                <div className={`${classes.cardInfo} ${classes.containerTotalReports}`}>
                    <Typography className={`${classes.cardInfoTitle} ${classes.totalReportsTitle}`}>Total Reports</Typography>
                    <div className={classes.containerCardInfoValue}>
                        <Typography className={classes.cardInfoValue}>{totalReports}</Typography>
                    </div>
                </div>

                <div className={`${classes.cardInfo} ${classes.containerTotalResolvedReports}`}>
                    <Typography className={`${classes.cardInfoTitle} ${classes.totalResolvedReportsTitle}`}>Total Resolved</Typography>
                    <div className={classes.containerCardInfoValue}>
                        <Typography className={classes.cardInfoValue}>{totalResolved}</Typography>
                    </div>
                </div>

                <div className={`${classes.cardInfo} ${classes.containerTotalPendingReports}`}>
                    <Typography className={`${classes.cardInfoTitle} ${classes.totalPendingReportsTitle}`}>Total Pending</Typography>
                    <div className={classes.containerCardInfoValue}>
                        <Typography className={classes.cardInfoValue}>{totalPending}</Typography>
                    </div>
                </div>

                <div className={`${classes.cardInfo} ${classes.containerAverageSolvingTime}`}>
                    <Typography className={`${classes.cardInfoTitle} ${classes.averageSolvingTimeTitle}`}>Average Solving Time</Typography>
                    <div className={classes.containerCardInfoValue}>
                        <Typography className={classes.cardInfoValue}>{avgDays > 0 ? `${avgDays}d` : 'N/A'}</Typography>
                    </div>
                </div>
            </div>

            <Typography className={classes.reportsInfoTitle}>Reports Types</Typography>
            <div className={classes.containerReportsTypes}>
                <div className={classes.cardReportType}>
                    <Typography className={classes.cardReportTypeTitle}>Cheating</Typography>
                    <div className={classes.containerReportTypeValue}>
                        <Typography className={classes.reportTypeValue}>{cheatingCount}</Typography>
                    </div>
                </div>
                <div className={classes.cardReportType}>
                    <Typography className={classes.cardReportTypeTitle}>Griefing</Typography>
                    <div className={classes.containerReportTypeValue}>
                        <Typography className={classes.reportTypeValue}>{griefingCount}</Typography>
                    </div>
                </div>
                <div className={classes.cardReportType}>
                    <Typography className={classes.cardReportTypeTitle}>Smurfing</Typography>
                    <div className={classes.containerReportTypeValue}>
                        <Typography className={classes.reportTypeValue}>{smurfingCount}</Typography>
                    </div>
                </div>
                <div className={classes.cardReportType}>
                    <Typography className={classes.cardReportTypeTitle}>Abusive Communication</Typography>
                    <div className={classes.containerReportTypeValue}>
                        <Typography className={classes.reportTypeValue}>{abusiveCommsCount}</Typography>
                    </div>
                </div>
                <div className={classes.cardReportType}>
                    <Typography className={classes.cardReportTypeTitle}>Not a team player</Typography>
                    <div className={classes.containerReportTypeValue}>
                        <Typography className={classes.reportTypeValue}>{notTeamPlayerCount}</Typography>
                    </div>
                </div>
                <div className={classes.cardReportType}>
                    <Typography className={classes.cardReportTypeTitle}>Non communicative player</Typography>
                    <div className={classes.containerReportTypeValue}>
                        <Typography className={classes.reportTypeValue}>{notVocalCount}</Typography>
                    </div>
                </div>
            </div>
        </div>
    );
}